import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./locales/en.json";

type SupportedLanguage = "en" | "es";

const isSupportedLanguage = (value: string): value is SupportedLanguage =>
  value === "en" || value === "es";

const normalizeLanguage = (
  value: string | null | undefined,
): SupportedLanguage => (value === "es" ? "es" : "en");

// Get stored language preference to avoid flash
const getStoredLanguage = (): SupportedLanguage => {
  try {
    const stored = localStorage.getItem("i18nextLng");
    if (stored && isSupportedLanguage(stored)) {
      return stored;
    }
  } catch {
    // localStorage not available
  }
  return "en";
};

let esResourcePromise: Promise<Record<string, unknown>> | null = null;

const loadEsResource = async (): Promise<Record<string, unknown>> => {
  if (!esResourcePromise) {
    esResourcePromise = import("./locales/es.json").then(
      (module) => module.default as Record<string, unknown>,
    );
  }

  return esResourcePromise;
};

const initialLanguage = getStoredLanguage();
const initialResources: Record<
  string,
  { translation: Record<string, unknown> }
> = {
  en: { translation: en as Record<string, unknown> },
};

if (initialLanguage === "es") {
  initialResources.es = { translation: await loadEsResource() };
}

i18n.use(initReactI18next).init({
  resources: initialResources,
  lng: initialLanguage,
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
});

const originalChangeLanguage = i18n.changeLanguage.bind(i18n);

i18n.changeLanguage = ((
  language?: string,
  callback?: (...args: any[]) => any,
) => {
  const nextLanguage = normalizeLanguage(language);
  const ensureLanguage =
    nextLanguage === "es" && !i18n.hasResourceBundle("es", "translation")
      ? loadEsResource().then((resource) => {
          i18n.addResourceBundle("es", "translation", resource, true, true);
        })
      : Promise.resolve();

  return ensureLanguage.then(() =>
    originalChangeLanguage(nextLanguage, callback),
  );
}) as typeof i18n.changeLanguage;

// Persist language changes to localStorage
i18n.on("languageChanged", (lng) => {
  try {
    localStorage.setItem("i18nextLng", normalizeLanguage(lng));
  } catch {
    // localStorage not available
  }
});

export default i18n;
